import { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import {
  getArticleBySlug,
  getRelatedArticles,
  getCityNameFromSlug,
} from '../../utils/articleData';
import { isValidCitySlug } from '../../utils/routeGenerator';
import { track } from '../../utils/tracking';
import InArticleAd from '../../components/feature/InArticleAd';
import ForSaleArticleBlock from '../../components/feature/ForSaleArticleBlock';

export default function ArticlePage() {
  // ----------- Params ----------
  // Generic type for useParams must be written without HTML entities.
  const { citySlug, articleSlug } = useParams<{
    citySlug: string;
    articleSlug: string;
  }>();
  const [copied, setCopied] = useState(false);

  // ----------- Validation ----------
  const isValidCity = citySlug && articleSlug && isValidCitySlug(citySlug);
  const article = isValidCity ? getArticleBySlug(citySlug, articleSlug) : null;
  const cityName = isValidCity ? getCityNameFromSlug(citySlug) : '';
  const relatedArticles = isValidCity && article ? getRelatedArticles(citySlug, articleSlug, 3) : [];

  // ----------- Tracking ----------
  useEffect(() => {
    if (article && cityName) {
      track('article_view', {
        city: cityName,
        article_title: article.title,
        category: article.category,
      });
    }
  }, [cityName, article]);

  // ----------- Head & Meta ----------
  useEffect(() => {
    if (!article || !cityName) return;

    document.title = `${article.title} | ${cityName} Business Funding`;

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', article.excerpt);
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', article.keywords.join(', '));
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', window.location.href);
    }

    // Helper to create / update meta tags
    const upsertMeta = (
      selector: string,
      attributes: Record<string, string>,
      createTag = 'meta'
    ) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement(createTag);
        document.head.appendChild(el);
      }
      Object.entries(attributes).forEach(([k, v]) => el!.setAttribute(k, v));
    };

    // Open Graph
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: article.title });
    upsertMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: article.excerpt,
    });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: article.image });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: window.location.href });

    // Twitter Card
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: article.title });
    upsertMeta('meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: article.excerpt,
    });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: article.image });
  }, [article, cityName]);

  // ----------- Early Returns (after all hooks) ----------
  if (!isValidCity) {
    return <Navigate to="/cities" replace />;
  }

  if (!article) {
    return <Navigate to={`/cities/${citySlug}`} replace />;
  }

  // ----------- Structured Data ----------
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: new Date(article.date).toISOString(),
    dateModified: new Date(article.date).toISOString(),
    author: {
      '@type': 'Person',
      name: article.author.name,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Business Capital Guide',
      logo: {
        '@type': 'ImageObject',
        url: `${window.location.origin}/favicon.ico`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': window.location.href,
    },
    keywords: article.keywords.join(', '),
  };

  // ----------- Social Sharing ----------
  const handleShare = (platform: string) => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(article.title);

    let shareUrl = '';

    switch (platform) {
      case 'twitter':
        // Use plain '&' inside template literals; HTML entity is not needed.
        shareUrl = `https://twitter.com/intent/tweet?url=${url}&text=${title}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${url}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, '_blank', 'width=600,height=400');
    track('article_shared', {
      platform,
      article_title: article.title,
      city: cityName,
    });
  };

  // ----------- Copy Link ----------
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      track('article_link_copied', {
        article_title: article.title,
        city: cityName,
      });
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  // ----------- Render ----------
  return (
    <>
      {/* Structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <div className="min-h-screen bg-white">
        <Header />

        {/* Breadcrumb */}
        <div className="bg-gray-50 border-b border-gray-200 py-4 px-4">
          <div className="max-w-7xl mx-auto">
            <nav className="flex items-center gap-2 text-sm flex-wrap">
              <Link to="/" className="text-gray-600 hover:text-[#C22F3B] transition-colors">
                Home
              </Link>
              <i className="ri-arrow-right-s-line text-gray-400" />
              <Link to="/cities" className="text-gray-600 hover:text-[#C22F3B] transition-colors">
                Cities
              </Link>
              <i className="ri-arrow-right-s-line text-gray-400" />
              <Link to={`/cities/${citySlug}`} className="text-gray-600 hover:text-[#C22F3B] transition-colors">
                {cityName}
              </Link>
              <i className="ri-arrow-right-s-line text-gray-400" />
              <span className="text-gray-900 font-semibold">{article.title}</span>
            </nav>
          </div>
        </div>

        {/* Article Hero */}
        <section className="py-12 px-4 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-4xl mx-auto">
            <div className="mb-6">
              <span className="inline-block px-4 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold rounded-full">
                {article.category}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-6 text-gray-600 mb-8 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-amber-500 to-orange-500 rounded-full text-white font-bold">
                  {article.author.name.charAt(0)}
                </div>
                <div>
                  <div className="font-semibold text-gray-900">{article.author.name}</div>
                  <div className="text-sm text-gray-500">{article.author.role}</div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-1">
                  <i className="ri-calendar-line" />
                  <span>{article.date}</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-time-line" />
                  <span>{article.readTime}</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Article Image */}
        <section className="px-4 mb-12">
          <div className="max-w-5xl mx-auto">
            <div className="relative w-full h-96 rounded-2xl overflow-hidden">
              <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top" />
            </div>
          </div>
        </section>

        {/* Article Content with Sidebar */}
        <section className="px-4 pb-20">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Main Content */}
              <div className="lg:col-span-2">
                <article className="prose prose-lg max-w-none">
                  <div
                    className="text-gray-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: article.content }}
                  />
                </article>

                {/* In-Article Ad */}
                <InArticleAd />

                {/* For Sale Block */}
                <ForSaleArticleBlock />

                {/* Article Footer */}
                <div className="mt-12 pt-8 border-t border-gray-200">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Share this article</h3>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleShare('twitter')}
                          className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-[#1DA1F2] text-gray-600 hover:text-white rounded-lg transition-colors cursor-pointer"
                          aria-label="Share on Twitter"
                        >
                          <i className="ri-twitter-x-line text-lg" />
                        </button>
                        <button
                          onClick={() => handleShare('facebook')}
                          className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-[#1877F2] text-gray-600 hover:text-white rounded-lg transition-colors cursor-pointer"
                          aria-label="Share on Facebook"
                        >
                          <i className="ri-facebook-fill text-lg" />
                        </button>
                        <button
                          onClick={() => handleShare('linkedin')}
                          className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-[#0A66C2] text-gray-600 hover:text-white rounded-lg transition-colors cursor-pointer"
                          aria-label="Share on LinkedIn"
                        >
                          <i className="ri-linkedin-fill text-lg" />
                        </button>
                        <button
                          onClick={handleCopyLink}
                          className="w-10 h-10 flex items-center justify-center bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors cursor-pointer relative"
                          aria-label="Copy link"
                        >
                          <i className={copied ? 'ri-check-line text-green-600' : 'ri-link text-lg'} />
                          {copied && (
                            <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                              Copied!
                            </span>
                          )}
                        </button>
                      </div>
                    </div>
                    <Link
                      to={`/cities/${citySlug}`}
                      className="inline-flex items-center gap-2 text-amber-600 font-semibold hover:gap-3 transition-all"
                    >
                      <i className="ri-arrow-left-line" />
                      Back to {cityName}
                    </Link>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-8 space-y-8">
                  {/* CTA Card */}
                  <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-8 text-white">
                    <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-lg mb-4">
                      <i className="ri-file-list-3-line text-2xl" />
                    </div>
                    <h3 className="text-xl font-bold mb-3">Ready to Explore Funding Options?</h3>
                    <p className="text-white/90 mb-6 text-sm leading-relaxed">
                      See what funding options may be available for your {cityName} business.
                    </p>
                    <Link
                      to={`/cities/${citySlug}/prequalification`}
                      onClick={() =>
                        track('cta_clicked', {
                          cta: 'article_sidebar',
                          city: cityName,
                          article: article.title,
                        })
                      }
                      className="block w-full text-center px-6 py-3 bg-white text-amber-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap"
                    >
                      Get Pre-Qualified
                    </Link>
                  </div>

                  {/* Quick Links */}
                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Links</h3>
                    <div className="space-y-3">
                      <Link
                        to={`/cities/${citySlug}`}
                        className="flex items-center gap-3 text-gray-700 hover:text-amber-600 transition-colors group"
                      >
                        <i className="ri-map-pin-line text-amber-600" />
                        <span className="group-hover:translate-x-1 transition-transform">{cityName} Hub</span>
                      </Link>
                      <Link
                        to="/business-funding"
                        className="flex items-center gap-3 text-gray-700 hover:text-amber-600 transition-colors group"
                      >
                        <i className="ri-funds-line text-amber-600" />
                        <span className="group-hover:translate-x-1 transition-transform">Funding Options</span>
                      </Link>
                      <Link
                        to="/cities"
                        className="flex items-center gap-3 text-gray-700 hover:text-amber-600 transition-colors group"
                      >
                        <i className="ri-building-line text-amber-600" />
                        <span className="group-hover:translate-x-1 transition-transform">All Cities</span>
                      </Link>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-4">Topics</h3>
                    <div className="flex flex-wrap gap-2">
                      {article.keywords.map((keyword, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                        >
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-20 px-4 bg-gray-50">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-900 mb-12">
                More Articles from {cityName}
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {relatedArticles.map((relatedArticle) => (
                  <article
                    key={relatedArticle.id}
                    className="bg-white rounded-xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all group"
                  >
                    <div className="relative w-full h-48 overflow-hidden">
                      <img
                        src={relatedArticle.image}
                        alt={relatedArticle.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-4 left-4">
                        <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                          {relatedArticle.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                        <div className="flex items-center gap-1">
                          <i className="ri-calendar-line" />
                          <span>{relatedArticle.date}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <i className="ri-time-line" />
                          <span>{relatedArticle.readTime}</span>
                        </div>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-amber-700 transition-colors line-clamp-2">
                        {relatedArticle.title}
                      </h3>
                      <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-3">
                        {relatedArticle.excerpt}
                      </p>
                      <Link
                        to={`/cities/${citySlug}/articles/${relatedArticle.slug}`}
                        className="inline-flex items-center gap-2 text-amber-600 font-semibold text-sm hover:gap-3 transition-all"
                      >
                        Read Article
                        <i className="ri-arrow-right-line" />
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        <Footer />
      </div>
    </>
  );
}
